import { useCallback, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TranslationKey } from "@/i18n/types";

interface RecoverableErrorBannerProps {
  messageKey: TranslationKey;
  /** Resolves when the retry finishes; rejection is treated as "still failing". */
  onRetry: () => Promise<unknown>;
  /**
   * Whether the failure is still present after a retry. Read AFTER `onRetry` settles so
   * the spinner is only cleared when the banner is going to stay on screen.
   */
  stillFailing: () => boolean;
}

/**
 * Non-blocking banner for a bootstrap step that failed but did not stop the app.
 *
 * Deliberately non-blocking: none of these failures mean the user has lost anything, so
 * none should become a modal standing between them and the app. But they must be
 * VISIBLE and RETRYABLE — a store flag with no consumer is indistinguishable from having
 * no error handling at all, which is how a subscriber ends up silently shown the free
 * tier with nothing to click.
 */
export const RecoverableErrorBanner = ({
  messageKey,
  onRetry,
  stillFailing,
}: RecoverableErrorBannerProps) => {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      // The banner unmounts on success, so only clear when the error is still present —
      // otherwise this sets state on an unmounted component.
      if (stillFailing()) setRetrying(false);
    }
  }, [onRetry, stillFailing]);

  return (
    <div
      role="status"
      className="flex items-start gap-2 border-b border-yellow-200 bg-yellow-50 px-4 py-2"
    >
      <AlertCircle
        className="mt-0.5 size-4 shrink-0 text-yellow-600"
        aria-hidden="true"
      />
      <p className="flex-1 text-sm font-normal font-inter text-yellow-800">
        {t(messageKey)}
      </p>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retrying}
        // Explicit focus ring: the browser default outline is the only affordance on a
        // raw button, and blue-on-yellow is hard to see against this banner.
        className="flex items-center gap-1 rounded-sm text-sm font-semibold font-inter text-yellow-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-700 focus-visible:ring-offset-1 focus-visible:ring-offset-yellow-50 disabled:no-underline disabled:opacity-60"
      >
        {retrying && <Loader2 className="size-3.5 animate-spin" />}
        {t("common.retry")}
      </button>
    </div>
  );
};

export default RecoverableErrorBanner;
